import { LIMIT_SIZE_1000 } from '@/main/app.constants';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import { setModelVersionState } from '@/tools/models/model.actions';
import { fetchModel, getDerivedFeatureId } from '@/tools/models/model.utilities';
import { getDependencies } from '@/utilities/formula.utilities';
import { errorToast } from '@/utilities/toast.utilities';
import { sqFormulasApi } from '@seeqdev/api/endpoint/FormulasApi';
import { sqItemsApi } from '@seeqdev/api/endpoint/ItemsApi';
import { sqModelsApi } from '@seeqdev/api/endpoint/ModelsApi';
import type {
  FormulaOutput,
  ModelType,
  ModelVersionMetadata,
  NewlyAddedDerivedFeature,
} from '@/tools/models/model.types';
import type { OutputDependencyItem } from '@/tools/models/model.types';
import type { ModelInputV1 } from '@seeqdev/api/model/ModelInputV1';

/** Edits a specific model version and publishes it as the new production version. */
export const editAndPublishModel = async (modelId: string, versionId: string, input: ModelInputV1) => {
  await sqModelsApi.editModelVersion(input, { modelId, versionId });
  await sqModelsApi.publishModelVersion({ versionId }, { modelId });
};

/** Saves changes to a draft without locking or publishing. */
export const saveDraft = async (modelId: string, versionId: string, input: ModelInputV1) => {
  await sqModelsApi.editModelVersion(input, { modelId, versionId });
};

/** Fetches a model for editing, populating store state and marking inputs as collapsed.
 * Transparently creates a draft so edits are versioned. If a draft already exists it is
 * reused; if the model has no version history yet the initial unlocked model is loaded directly.
 */
export const fetchModelForEditing = async (id: string, modelType: ModelType) => {
  // Try to resume an existing draft first
  try {
    const { data: draft } = await sqModelsApi.getDraft({ modelId: id });
    await fetchModel(draft.versionId!, modelType, true, true);
    await populateSourceVersionNumber(id, modelType);
    return;
  } catch (error) {
    const status = (error as any)?.response?.status;
    if (status !== 404) {
      throw error;
    }
  }

  // No draft exists — create one
  try {
    const { data: draft } = await sqModelsApi.createDraft({ modelId: id });
    await fetchModel(draft.versionId!, modelType, true, true);
    await populateSourceVersionNumber(id, modelType);
  } catch (error) {
    const status = (error as any)?.response?.status;
    if (status === 400) {
      // No version history yet — edit the initial unlocked model directly
      await fetchModel(id, modelType, true, true);
      setModelVersionState(modelType, { isLocked: false, isDraft: false, sourceVersionNumber: undefined });
      return;
    }
    throw error;
  }
};

/**
 * Fetches the version list for a model container and sets the source version
 * number (production version) on the store. Called after loading a draft for editing.
 */
async function populateSourceVersionNumber(modelId: string, modelType: ModelType) {
  try {
    const { data } = await sqModelsApi.getModelVersions({ modelId });
    const productionVersion = data.versions.find((v) => v.production);
    setModelVersionState(modelType, {
      isLocked: false,
      isDraft: true,
      sourceVersionNumber: productionVersion?.version,
    });
  } catch {
    // If version fetch fails, still mark as draft
    setModelVersionState(modelType, { isLocked: false, isDraft: true, sourceVersionNumber: undefined });
  }
}

/**
 * Fetches version and draft metadata for a single model container.
 * Returns null if version data cannot be fetched (e.g. no Locking license).
 */
export const fetchModelVersionMetadata = async (modelId: string): Promise<ModelVersionMetadata> => {
  const [versionsResult, draftResult] = await Promise.allSettled([
    sqModelsApi.getModelVersions({ modelId }),
    sqModelsApi.getDraft({ modelId }),
  ]);

  const versions = versionsResult.status === 'fulfilled' ? versionsResult.value.data.versions : [];

  const hasDraft = draftResult.status === 'fulfilled';
  const draftVersionId = hasDraft ? draftResult.value.data.versionId : undefined;

  const productionVersion = versions.find((v) => v.production);

  return {
    hasDraft,
    draftVersionId,
    productionVersionNumber: productionVersion?.version,
    versionCount: versions.length,
    isLocked: versions.length > 0,
    versions,
  };
};

/**
 * Fetches version and draft metadata for multiple model containers in parallel.
 * Returns a map of modelId -> ModelVersionMetadata.
 */
export const fetchAllModelVersionMetadata = async (modelIds: string[]): Promise<Map<string, ModelVersionMetadata>> => {
  const results = await Promise.allSettled(
    modelIds.map((id) => fetchModelVersionMetadata(id).then((metadata) => ({ id, metadata }))),
  );

  const metadataMap = new Map<string, ModelVersionMetadata>();
  for (const result of results) {
    if (result.status === 'fulfilled') {
      metadataMap.set(result.value.id, result.value.metadata);
    }
  }
  return metadataMap;
};

/** Fetches model container items scoped to the given workbook IDs. */
export async function fetchModelItems(workbookIds: string[]) {
  const types = [SeeqNames.Types.ModelContainer];
  const filters = [
    `${SeeqNames.Properties.DatasourceClass}==${SeeqNames.LocalDatasources.Models.DatasourceClass} && ${SeeqNames.Properties.DatasourceId}==${SeeqNames.LocalDatasources.Models.User.DatasourceId}`,
  ];
  const { data } = await sqItemsApi.searchItems({
    types,
    filters,
    scope: workbookIds,
    limit: LIMIT_SIZE_1000,
  });
  return data.items ?? [];
}

/** Fetches the saved formula for each derived feature that has an id. Errors are swallowed
 * (with a toast) so a single failure does not abort the rest of the batch. Returns a map keyed
 * by featureId for callers that need to look up formulas during model formatting. */
export const fetchFormulasForFeatures = async (
  derivedFeatures: NewlyAddedDerivedFeature[],
): Promise<Map<string, FormulaOutput>> => {
  const formulasByFeatureId = new Map<string, FormulaOutput>();
  await Promise.all(
    derivedFeatures.map(async (feature) => {
      const featureId = getDerivedFeatureId(feature);
      if (!featureId) return;

      // Skip if feature already has a formulaCreator rule
      const existingFormulaCreator = feature?.rules?.[0]?.formulaCreator;
      if (existingFormulaCreator) return;

      try {
        const response = await sqFormulasApi.getItem({ id: featureId });
        if (response?.data) formulasByFeatureId.set(featureId, response.data);
      } catch (error) {
        errorToast({ httpResponseOrError: error });
      }
    }),
  );
  return formulasByFeatureId;
};

/** Fetches the dependencies of the given parameters and returns a deduped list of dependencies. */
export const fetchDependenciesOfParameters = async (
  parameterItemIds: string[],
): Promise<{
  dependencyList: OutputDependencyItem[];
  dependenciesByParameterId: Map<string, OutputDependencyItem[]>;
}> => {
  const dependenciesByParameterId = new Map<string, OutputDependencyItem[]>();
  const allDependenciesById = new Map<string, OutputDependencyItem>();

  // Dedupe parameter IDs to avoid redundant API calls
  const uniqueParameterIds = [...new Set(parameterItemIds)];

  await Promise.all(
    uniqueParameterIds.map(async (id) => {
      const dependencies = (await getDependencies({ id }))?.dependencies ?? [];
      dependenciesByParameterId.set(id, dependencies);
      dependencies.forEach((dep: OutputDependencyItem) => allDependenciesById.set(dep.id, dep));
    }),
  );

  return { dependencyList: [...allDependenciesById.values()], dependenciesByParameterId };
};
